/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */


'use strict';

import {
    EntityBase,
    ClientProperties
} from "./entitybase";
import * as tools from '../utils/tools';
import {
    AggregateTypes,
    ViewType,
    DataVokeSummaryItemType,
    EntityTypes
} from "../utils/dvtypes";

class ViewDefinition {
    /**
     * Create a new View Definition object
     * @class
     * @classdesc View Definition Object
     */
    constructor () {
        this.AnalysisGroupByPropertyIDs = [];
        this.AppCategory = "";
        this.AppName = "";
        this.AutoGenNewRecordCodePrefix = null;
        this.AutoGenNewRecordCodeSuffix = null;
        this.AutoGenNewRecordCodes = false;
        this.AutoSelectFirstRow = true;
        this.ClientVersion = app.version;
        this.ColFixedCnt = 0;
        this.ColumnFixed = 0;
        this.ColumnProperties = [];
        this.Comment = null;
        this.CurrentVersion = "::0";
        this.DefaultFormView = true;
        this.Description = "";
        this.DisableClosePanel = false;
        this.DisableLinking = false;
        this.DisableNewRecords = false;
        this.ForceFocus = false;
        this.FormLayout = null;
        this.GroupIDs = [];
        this.HideEditButtons = false;
        this.HideHeaderInLayout = false;
        this.HideIfNoRecords = false;

        /**
         * Flag to indicate if the loading panel should be suppressed for the form
         * @type {boolean}
         */
        this.HideLoadPanel = undefined;
        this.IgnoreStatusStyling = true;

        this.HideStatusTooltipText = true;
        this.IndexPropertyID = null;
        this.InternalDesc = null;
        this.IsDetailView = false;
        this.IsSelectionView = false;
        this.IsShared = true;
        this.IsStandardView = false;
        this.IsReportView = false;
        this.LayoutRootOrientation = 0;
        this.LayoutViewList = null;
        this.ListeningToViews = null;
        this.Name = "";
        this.NoRecordsText = "There are no records to display.";
        this.OfflineEnabled = false;
        this.OriginalVersion = "::0";

        /**
         * Default to undefined to show that it isn't set. Otherwise will be the number of records that are pulled down
         * @type {number|undefined}
         */
        this.PageRecordSize = undefined;
        this.PivotProperties = [];
        this.PrimaryViewID = null;
        this.RecordTypeIDs = [];
        this.ReportDefinition = null;

        // these are set as undefined so that they will not be serialized unnecessarily by JSON when the entity is saved.
        this.OnBeforeClose = undefined;
        this.OnDataLoaded = undefined;
        this.OnInit = undefined;
        this.OnOpen = undefined;
        this.OnRowDoubleClick = undefined;
        this.OnSelectionChanged = undefined;

        this.ShowCountOnTile = false;
        this.ShowFilterBar = false;
        this.ShowGroupSummary = false;
        this.ShowLinkedToUser = false;
        this.ShowSearchPanel = false;
        this.ShowSummary = false;
        this.SuppressInitDataLoad = false;
        this.TaskDisplay = null;
        this.TaskNavViewID = null;
        this.Type = ViewType.GridView;
        this.UserCreated = "";
        this.UserLastModified = "";
        this.ViewDimensionType = 0;
        this.ViewHeight = 0;
        this.ViewMacros = [];
        this.ViewWidth = 0;
        this.IncludeDeletedRecords = false;
    }
}

class ColumnProperty {
    /**
     * Create a new Column Property object
     * @param {EntityLinker|number|null} [propertyID = null] - The ID of the property that this column is for.
     * @class
     * @classdesc Column Property Object
     */
    constructor (propertyID) {
        /**
         * Property ID for the underlying Property that this column property defines.
         * @type {EntityLinker|null}
         */
        this.PropertyID = null;
        if (propertyID != null) {
            if (typeof propertyID === 'object' && propertyID.UID != null) {
                // this is already an Entity linker (not null, not a number)
                this.PropertyID = propertyID;
            }
            else {
                // attempt to get the property specified.
                this.PropertyID = tools.createEntityLinker("Property", app.dv.cache.properties.allProperties.getVal(propertyID).UID)
            }
        }

        /**
         * The default filter value...AL
         * @type {string}
         */
        this.FilterValue = '';

        /**
         * The defalut filter operator...AL
         * @type {string}
         */
        this.Op = '';

        /**
         * The default sort direction (optional)...AL
         * @type {string}
         */
        this.Sort = '';

        /**
         * If true, the grid will group on this property...AL
         * @type {boolean} - FALSE
         */
        this.IsGrouped = false;

        /**
         * If true, the column will be hidden...AL
         * @type {boolean} - FALSE
         */
        this.IsHidden = false;

        /**
         * If true, the values in this column will be read only...AL
         * @type {boolean} - FALSE
         */
        this.IsReadOnly = false;

        /**
         * If true, this column's values will be flagged for required (will not allow saves without a present value)...AL
         * @type {boolean} - FALSE
         */
        this.IsRequired = false;

        /**
         * If this is an analysis property, and IsDrillIn is set to true, then the user can click into the underlying view that drives the current grouping...AL
         * @type {boolean} - FALSE
         */
        this.IsDrillIn = false;

        /**
         * The RecordType ID for the linked property (if defined)...AL
         * @type {EntityLinker|number|null}
         */
        this.RecordTypeID = null;

        /**
         * An alias for property. This is used in the text for a grid column.
         * @type {string}
         */
        this.Alias = '';

        /**
         * The view ID for the view that drives the linked property to allow filtering on a dropedown list (as defined by the driving view)...AL
         * @type {EntityLinker|number|null}
         */
        this.DropDownViewID = null;

        /**
         * If defined, this will define the default width of the column...AL
         * @type {number|null}
         */
        this.DefaultWidth = null;

        /**
         * The summary aggregate type that will be used to calculate summary values...AL
         * @type {DataVokeSummaryItemType}
         */
        this.AggregateSummary = DataVokeSummaryItemType.None;

        /**
         * Defines the display format that will show for this property in the summary calculation section...AL
         * @type {string}
         */
        this.DisplayFormatSummary = '';

        /**
         * The summary aggreate type that will be used to calculate summary group values...AL
         * @type {DataVokeSummaryItemType}
         */
        this.AggregateGroupSummary = DataVokeSummaryItemType.None;

        /**
         * This is the display format that will show for this property in the summary group calculation section...AL
         * @type {string}
         */
        this.DisplayFormatGroupSummary = '';

        /**
         * If true, the binary data for this property will automatically be downloaded on the initial pull...AL
         * @type {boolean} - FALSE
         */
        this.AutoDownloadBinary = false;
    }
}

export class PivotProperty {
    /**
     * Create a Pivot Property object for use in a pivot form.
     * @param {EntityLinker|number|null} [propertyID = null] - The ID of the underlying property that this pivot property is for.
     * @class
     * @classdesc Pivot Property Object
     */
    constructor (propertyID) {
        /**
         * Property ID for the underlying Property that this pivot property defines.
         * @type {EntityLinker|null}
         */
        this.PropertyID = null;
        if (propertyID != null) {
            if (typeof propertyID === 'object' && propertyID.UID != null) {
                // this is already an Entity linker (not null, not a number)
                this.PropertyID = propertyID;
            }
            else {
                // attempt to get the property specified.
                this.PropertyID = tools.createEntityLinker("Property", app.dv.cache.properties.allProperties.getVal(propertyID).UID)
            }
        }

        /**
         * The aggregate type that is used when pivoting (defult to count)...AL
         * @type {AggregateTypes} - AggregateTypes.Count
         */
        this.Aggregate = AggregateTypes.Count;

        /**
         * The having expression for the pivot property value...AL
         * @type {string}
         */
        this.HavingExpression = '';

        /**
         * If true, the values in this column will be read only...AL
         * @type {boolean} - FALSE
         */
        this.IsReadOnly = false;

        /**
         * An alias for the linked property...AL
         * @type {string}
         */
        this.Alias = '';

        /**
         * The summary aggregate type that will be used to calculate summary values...AL
         * @type {DataVokeSummaryItemType} - DataVokeSummaryItemType.None
         */
        this.AggregateSummary = DataVokeSummaryItemType.None;

        /**
         * Defines the display format that will show for this property in the summary caclulation section...AL
         * @type {string}
         */
        this.DisplayFormatSummary = '';
    }
}

export class View extends EntityBase {
    /**
     * Create a new view entity object.
     * @param {EntityLinker|number} rtID - Entity linker or ID number of the record type (table) to base this view on.
     * @param {Array} properties - Array of property entity objects to add to the view.
     * @class
     * @classdesc View Entity Object
     */
    constructor (rtID, properties) {
        super();

        /**
         * Create a new temp ID (negative counter in tools)...AL
         * @type {number}
         */
        this.ViewID = tools.tempID();

        /**
         * Create a new default definition...AL
         * @type {ViewDefinition}
         */
        this.Definition = new ViewDefinition();

        // If paramenter(s) where passed in, the caller is at least specifying record type id...AL
        if (rtID != null) {
            this.Definition.RecordTypeIDs.push(rtID);

            // If a list of properties were passed in, use them to create this view's column properties...AL
            if (Array.isArray(properties)) {
                for (let i = 0, limit = properties.length; i < limit; i++) {
                    this.Definition.ColumnProperties.push(new ColumnProperty(tools.createEntityLinker(EntityTypes.Property, properties[i])));
                }
            }
        }

        this.clientProperties = new ClientProperties(EntityTypes.View);

    }
}

/**
 * Create a new View object using the record type and properties specified.
 * @param {number} rtID - ID number of the record type to base this view off of.
 * @param {Array} properties - Array of property objects.
 * @returns {View}
 */
export function create (rtID, properties) {
    return new View(rtID, properties);
}

/**
 * Create a new base view definition object.
 * @returns {ViewDefinition}
 */
export function newDefinition() {
    return new ViewDefinition();
}

/**
 * Create a new base column property object using the property ID specified
 * @param {EntityLinker|number|null} propertyID - The ID of the property to base this column off of.
 * @returns {ColumnProperty}
 */
export function newColumnProperty (propertyID) {
    return new ColumnProperty(propertyID);
}

export function newPivotProperty (propertyID) {
    return new PivotProperty(propertyID);
}
